<template>
  <div class="do-dining-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('doDining.page.title')" :subtitle="$t('doDining.page.subtitle')" />

    <el-row :gutter="16">
      <!-- 左侧：商品选择 + 扫码核销 -->
      <el-col :xs="24" :md="8" :lg="6">
        <div class="ds-panel ds-panel--padded ds-goods-panel">
          <div class="ds-panel__header">
            <span class="ds-panel__title">{{ $t('doDining.selectGoods') }}</span>
          </div>
          <div class="ds-goods-grid">
            <div v-for="reserveDining in doDiningInfo.reserveDinings" :key="reserveDining.goodsId"
              class="ds-goods-item"
              :class="{ 'ds-goods-item--active': doDiningInfo.goodsId == reserveDining.goodsId }"
              @click="_chooseDining(reserveDining)">
              <el-image class="ds-goods-item__img" :src="reserveDining.imgUrl || '/img/noPhoto.jpg'" fit="cover" />
              <div class="ds-goods-item__name">{{ reserveDining.goodsName }}</div>
            </div>
          </div>
          <el-empty v-if="!doDiningInfo.reserveDinings.length" :description="$t('doDining.page.noGoods')"
            :image-size="80" />
        </div>
      </el-col>

      <!-- 右侧：扫码核销 + 订餐记录 -->
      <el-col :xs="24" :md="16" :lg="18">
        <div class="ds-panel ds-panel--padded ds-scan-panel">
          <el-row :gutter="16">
            <el-col :xs="24" :sm="18">
              <el-input v-model="doDiningInfo.qrCode" id="qrCode"
                :placeholder="$t('doDining.scanQrCode')" @keyup.enter.native="_doDining()" />
            </el-col>
            <el-col :xs="24" :sm="6">
              <el-button type="primary" class="w-full" @click="_doDining()">
                {{ $t('doDining.dining') }}
              </el-button>
            </el-col>
          </el-row>
        </div>

        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="doDiningInfo.conditions.startDate" type="datetime" class="w-full"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('doDining.selectStartTime')" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="doDiningInfo.conditions.endDate" type="datetime" class="w-full"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('doDining.selectEndTime')" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="doDiningInfo.conditions.nameLike" clearable
                :placeholder="$t('doDining.enterOwnerName')" @keyup.enter.native="_queryData()" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="doDiningInfo.conditions.dinerName" clearable
                :placeholder="$t('doDining.enterDinerName')" @keyup.enter.native="_queryData()" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="_queryData">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="_resetData">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="_queryData">
            <template #left>
              <span class="ds-panel__title">{{ $t('doDining.diningRecords') }}</span>
            </template>
            <template #right>
              <el-button type="primary" size="mini" icon="el-icon-download" @click="_exportExcel">
                {{ $t('common.export') }}
              </el-button>
            </template>
          </ds-toolbar>

          <ds-data-table :data="doDiningInfo.orders" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('doDining.page.empty')" @retry="_queryData">
            <el-table-column prop="createTime" :label="$t('doDining.diningTime')" align="center" width="170">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="goodsName" :label="$t('doDining.goods')" align="center" min-width="140" show-overflow-tooltip />
            <el-table-column prop="ownerName" :label="$t('doDining.owner')" align="center" width="120" />
            <el-table-column prop="personName" :label="$t('doDining.diner')" align="center" width="120" />
            <el-table-column prop="personTel" :label="$t('doDining.dinerPhone')" align="center" width="140">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.personTel || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="orderId" :label="$t('doDining.orderNo')" align="center" width="180">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.orderId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="remark" :label="$t('doDining.remark')" align="center" min-width="140" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.remark || '-' }}
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
            @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </el-col>
    </el-row>

    <add-reserve-dining-person ref="addReserveDiningPerson" />
    <delete-reserve-dining ref="deleteReserveDining" />
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import {
  listReserveGoods,
  queryOwnerDining,
  exportData,
  doDining
} from '@/api/scm/doDiningApi'
import AddReserveDiningPerson from '@/components/scm/addReserveDiningPerson'
import DeleteReserveDining from '@/components/scm/deleteReserveDining'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'DoDiningList',
  components: {
    AddReserveDiningPerson,
    DeleteReserveDining,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      communityId: '',
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      doDiningInfo: {
        reserveDinings: [],
        orders: [],
        total: 0,
        records: 1,
        goodsId: '',
        moreCondition: false,
        qrCode: '',
        conditions: {
          goodsName: '',
          startDate: '',
          endDate: '',
          ownerName: '',
          dinerName: '',
          communityId: ''
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      focusInterval: null
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.doDiningInfo.conditions.communityId = this.communityId
    this._listReserveDinings(1, 200)
    this._listReserveConfirms(this.page.current, this.page.size)
  },
  mounted() {
    document.getElementById("qrCode").focus()
    this.focusInterval = setInterval(() => {
      document.getElementById("qrCode").focus()
    }, 5000)
  },
  beforeDestroy() {
    if (this.focusInterval) {
      clearInterval(this.focusInterval)
    }
  },
  methods: {
    _listReserveDinings(_page, _rows) {
      const params = {
        page: _page,
        row: _rows,
        communityId: this.communityId
      }
      listReserveGoods(params)
        .then(response => {
          this.doDiningInfo.reserveDinings = Array.isArray(response.data) ? response.data : []
        })
        .catch(error => {
          console.error('请求失败:', error)
        })
    },
    _chooseDining(_dining) {
      this.doDiningInfo.goodsId = _dining.goodsId
      document.getElementById("qrCode").focus()
    },
    _doDining() {
      const data = {
        communityId: this.communityId,
        goodsId: this.doDiningInfo.goodsId,
        qrCode: this.doDiningInfo.qrCode
      }

      doDining(data)
        .then(response => {
          this.doDiningInfo.qrCode = ''
          if (response.code !== 0) {
            this.$message.error(response.msg)
            return
          }
          this._listReserveConfirms(this.page.current, this.page.size)
        })
        .catch(error => {
          console.error('请求失败:', error)
          this.doDiningInfo.qrCode = ''
        })
    },
    _listReserveConfirms(_page, _rows) {
      const params = {
        page: _page,
        row: _rows,
        ...this.doDiningInfo.conditions
      }
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      queryOwnerDining(params)
        .then(response => {
          this.doDiningInfo.total = response.total
          this.doDiningInfo.records = response.records
          this.doDiningInfo.orders = Array.isArray(response.data) ? response.data : []
          this.page.total = response.total || 0
        })
        .catch(error => {
          console.error('请求失败:', error)
          this.hasError = true
          this.errorMessage = error.message || this.$t('doDining.page.loadFailed')
        })
        .finally(() => {
          this.loading = false
        })
    },
    _queryData() {
      this.page.current = 1
      this._listReserveConfirms(this.page.current, this.page.size)
    },
    _resetData() {
      this.doDiningInfo.conditions.startDate = ''
      this.doDiningInfo.conditions.endDate = ''
      this.doDiningInfo.conditions.nameLike = ''
      this.doDiningInfo.conditions.dinerName = ''
      this.page.current = 1
      this._listReserveConfirms(this.page.current, this.page.size)
    },
    _exportExcel() {
      const params = {
        ...this.doDiningInfo.conditions,
        pagePath: 'doDining'
      }
      exportData(params)
        .then(response => {
          if (response.code === 0) {
            this.$router.push('/pages/property/downloadTempFile?tab=下载中心')
          }
        })
        .catch(error => {
          console.error('请求失败:', error)
        })
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this._listReserveConfirms(this.page.current, val)
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listReserveConfirms(val, this.page.size)
    }
  }
}
</script>

<style lang="scss" scoped>
.do-dining-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &--padded {
    padding: var(--ds-spacing-lg);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-md);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-scan-panel {
  padding: var(--ds-spacing-md) var(--ds-spacing-lg);
}

.ds-goods-grid {
  display: flex;
  flex-wrap: wrap;
  gap: var(--ds-spacing-sm);
}

.ds-goods-item {
  width: 76px;
  padding: 4px;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-sm);
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;

  &:hover {
    border-color: var(--ds-primary);
  }

  &--active {
    border-color: var(--ds-primary);
    background-color: var(--ds-primary-light-9, #f5f7fa);
  }

  &__img {
    width: 60px;
    height: 60px;
    border-radius: 4px;
    display: block;
  }

  &__name {
    margin-top: 4px;
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-700);
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
